/**
 * Market Drawer UI — New redesigned localization drawer
 *
 * Mobile:  Two native <select> elements + "Aplicar" button.
 *          Button starts disabled, enables when user changes a select.
 *          Submits country first (if changed), then language.
 * Desktop: Search-filtered country list + language pill grid.
 *          Works the same as the original localization-drawer.
 */
if (!customElements.get('market-drawer-ui')) {
  customElements.define('market-drawer-ui', class MarketDrawerUI extends Drawer {
    constructor() {
      super();

      // El contenido viene en un <template> y se crea al abrir por primera vez
      // (ver ensureContent). Si no hay template (versión antigua), iniciar ya.
      this.contentReady = false;
      if (!this.querySelector('template[data-market-content]')) this.initContent();
    }

    /**
     * Crea el contenido del drawer a partir del <template> la primera vez que se
     * necesita. Mantiene las listas de países e idiomas (~1.300 elementos) fuera
     * del DOM de cada página hasta que el usuario abre el drawer.
     */
    ensureContent() {
      if (this.contentReady) return;
      const template = this.querySelector('template[data-market-content]');
      if (template) template.replaceWith(template.content.cloneNode(true));
      this.initContent();
    }

    show(opener) {
      this.ensureContent();
      super.show(opener);
    }

    initContent() {
      this.contentReady = true;

      /* ── Desktop search ────────────────────────────────────── */
      this.searchInput = document.getElementById('Market-Search');
      if (this.searchInput) {
        this.regionCountry = document.getElementById('Market-Country-Filter-Results');
        this.regionLanguage = document.getElementById('Market-Language-Filter-Results');
        this.previousValue = '';
        this.searchInput.addEventListener('keyup', this.onSearchKeyup.bind(this));
      }

      /* ── Mobile selects + "Aplicar" button ─────────────────── */
      this.applyBtn = this.querySelector('.market-apply-btn');
      this.countrySelect = document.getElementById('MarketCountrySelect');
      this.languageSelect = document.getElementById('MarketLanguageSelect');

      if (this.applyBtn) {
        this.applyBtn.addEventListener('click', this.onApply.bind(this));
      }

      // Store initial values to detect changes
      this._initialCountry = this.countrySelect ? this.countrySelect.value : null;
      this._initialLanguage = this.languageSelect ? this.languageSelect.value : null;

      // Listen for select changes to toggle the button
      if (this.countrySelect) {
        this.countrySelect.addEventListener('change', this.onSelectChange.bind(this));
      }
      if (this.languageSelect) {
        this.languageSelect.addEventListener('change', this.onSelectChange.bind(this));
      }
    }

    /* ==================================================================
       Toggle "Aplicar" button enabled/disabled
       ================================================================== */
    onSelectChange() {
      if (!this.applyBtn) return;
      const countryChanged = this.countrySelect && this.countrySelect.value !== this._initialCountry;
      const languageChanged = this.languageSelect && this.languageSelect.value !== this._initialLanguage;

      if (countryChanged || languageChanged) {
        this.applyBtn.removeAttribute('disabled');
      } else {
        this.applyBtn.setAttribute('disabled', '');
      }
    }

    /* ==================================================================
       DESKTOP — Search filter (same logic as original drawer)
       ================================================================== */
    onSearchKeyup() {
      const searchValue = webvista.normalizeString(this.searchInput.value);
      if (searchValue === this.previousValue) return;
      this.previousValue = searchValue;

      // Filter countries
      const allCountries = this.querySelectorAll('.market-country-list > li');
      let visibleCountryCount = 0;
      allCountries.forEach(el => {
        const name = webvista.normalizeString(el.querySelector('.country').textContent);
        if (name.indexOf(searchValue) > -1) {
          el.classList.remove('hidden');
          visibleCountryCount++;
        } else {
          el.classList.add('hidden');
        }
      });
      if (this.regionCountry) {
        this.regionCountry.textContent = window['accessibilityStrings']['countrySelectorFilterCount']
          .replace('[count]', visibleCountryCount);
      }

      // Filter languages
      const allLanguages = this.querySelectorAll('.market-language-list > li');
      let visibleLanguageCount = 0;
      allLanguages.forEach(el => {
        const name = webvista.normalizeString(el.querySelector('.language').textContent);
        if (name.indexOf(searchValue) > -1) {
          el.classList.remove('hidden');
          visibleLanguageCount++;
        } else {
          el.classList.add('hidden');
        }
      });
      if (this.regionLanguage) {
        this.regionLanguage.textContent = window['accessibilityStrings']['languageSelectorFilterCount']
          .replace('[count]', visibleLanguageCount);
      }

      // Re-trap focus
      const drawerTrap = this.querySelector('[data-trap]');
      if (drawerTrap) webvista.trapFocus(drawerTrap, this.searchInput);
    }

    /* ==================================================================
       MOBILE — Apply button logic
       Shopify localization forms only accept one field at a time, so if
       both country AND language changed we submit country first, then on
       the next page load the saved language preference will be applied.
       For simplicity: we submit the changed form. If both changed, we
       submit country (which refreshes the page) and store the pending
       language in sessionStorage so it auto-submits on reload.
       ================================================================== */
    onApply() {
      const countryForm  = document.getElementById('MarketCountryForm-Mobile');
      const languageForm = document.getElementById('MarketLanguageForm-Mobile');

      const countryChanged  = this.countrySelect  && countryForm  && this.countrySelect.value  !== this._initialCountry;
      const languageChanged = this.languageSelect && languageForm && this.languageSelect.value !== this._initialLanguage;

      if (!countryChanged && !languageChanged) {
        this.hide();
        return;
      }

      if (countryChanged && languageChanged) {
        try { sessionStorage.setItem('market-drawer-ui--pending-locale', this.languageSelect.value); } catch(e) {}
      }

      if (countryChanged) {
        countryForm.querySelector('input[name="country_code"]').value = this.countrySelect.value;
        countryForm.submit();
        return;
      }

      if (languageChanged) {
        languageForm.querySelector('input[name="locale_code"]').value = this.languageSelect.value;
        languageForm.submit();
      }
    }

    /* ==================================================================
       Auto-apply pending language after country change reload
       ================================================================== */
    connectedCallback() {
      /* ── Redirect Localization-Drawer triggers to this drawer ──── */
      document.querySelectorAll('modal-opener[aria-controls="Localization-Drawer"]').forEach(opener => {
        opener.setAttribute('aria-controls', 'Market-Drawer');
        opener.modal = this;
      });

      /* ── Patch getElementById so scripts looking for the old ID find us ── */
      const oldDrawer = document.getElementById('Localization-Drawer');
      if (oldDrawer && typeof oldDrawer.hide === 'function') {
        oldDrawer.hide();
      }
      /* Expose a global ref so geolocation-banner / header-drawer can find it */
      window.__marketDrawerOverride = this;

      try {
        const pendingLocale = sessionStorage.getItem('market-drawer-ui--pending-locale');
        if (pendingLocale) {
          sessionStorage.removeItem('market-drawer-ui--pending-locale');
          this.ensureContent(); // los formularios de idioma están en el <template>
          const languageForm = document.getElementById('MarketLanguageForm-Mobile')
            || document.getElementById('MarketLanguageForm-Desktop');
          if (languageForm) {
            const input = languageForm.querySelector('input[name="locale_code"]');
            if (input && input.value !== pendingLocale) {
              input.value = pendingLocale;
              languageForm.submit();
            }
          }
        }
      } catch(e) {}
    }
  });
}
